import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { UploadManager, UploadRetryStatus } from "./UploadManager";
import ClosePageWarningModal from "../common/ClosePageWarningModal";
import { t } from "i18next";
import {
  endDeploymentUploadBeacon,
  EndUploadOutcome,
} from "../../api/deploymentsApi";
import { DeploymentStatus } from "../common/ObjectTypes";
import { setActiveUpload } from "./interruptedUpload";
import { startUploadHeartbeat, UploadHeartbeat } from "./uploadHeartbeat";
import { createScreenWakeLock } from "./screenWakeLock";

interface UploadContextType {
  projectId: number;
  projectGuid: string;
  deploymentId: number;
  deploymentGuid: string;
  deploymentName: string;
  inProgress: boolean;
  uploadIsPaused: boolean;
  /**
   * The manager ended its pass without completing (AKL-889): files were not uploaded, or
   * the SAS URL, the server's acceptance or a file's bytes could not be had. The file list
   * is kept; Resume continues, Cancel gives up.
   */
  uploadIsStopped: boolean;
  /** Files the manager gave up on in this pass — neither uploaded nor skipped (AKL-889). */
  failedFileCount: number;
  /**
   * The server has accepted `start_upload` for the upload in progress (AKL-823). False
   * while the SAS fetch and `start_upload` are pending, and for an upload stopped because
   * one of them failed — which the panel then says could not start.
   */
  uploadAccepted: boolean;
  /** What the manager is doing between two attempts at the same file (AKL-889). */
  retryStatus: UploadRetryStatus;
  selectedFiles: File[];
  uploadProgress: number;
  skippedFileCount: number;
  uploadedFileCount: number;
  startUpload: (manager: UploadManager) => void;
  pauseUpload: () => void;
  resumeUpload: () => void;
  cancelUpload: () => Promise<void>;
  failUpload: () => Promise<EndUploadOutcome | null>;
  endUpload: () => void;
  /**
   * Asks for the screen wake lock now, synchronously (AKL-1088). Call it from the click
   * that is about to start an upload, before any `await`: Safari grants the lock only
   * with user activation, and an upload starts from the folder picker's `change`, which
   * has none. The lock is kept for the upload that follows; call
   * `releaseUnusedScreenWakeLock` if none does. `resumeUpload` asks by itself.
   */
  requestScreenWakeLock: () => void;
  /** The click's upload did not start: lets go of the lock it asked for (AKL-1088). */
  releaseUnusedScreenWakeLock: () => void;
}

const UploadContext = createContext<UploadContextType | null>(null);

/** The Web Lock held while an upload is in progress; see the keep-alive effect. */
export const UPLOAD_WEB_LOCK_NAME = "akili-upload";

const noRetry: UploadRetryStatus = { kind: "none" };

export const UploadProvider: React.FC<React.PropsWithChildren<{}>> = ({
  children,
}) => {
  const [uploadManager, setUploadManager] = useState<null | UploadManager>(
    null,
  );
  const [uploadIsPaused, setUploadIsPaused] = useState(false);
  const [uploadIsStopped, setUploadIsStopped] = useState(false);
  const [failedFileCount, setFailedFileCount] = useState(0);
  const [retryStatus, setRetryStatus] = useState<UploadRetryStatus>(noRetry);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [skippedFileCount, setSkippedFileCount] = useState(0);
  const [uploadedFileCount, setUploadedFileCount] = useState(0);
  const [projectId, setProjectId] = useState<number>(0);
  const [projectGuid, setProjectGuid] = useState<string>("");
  const [deploymentId, setDeploymentId] = useState<number>(0);
  const [deploymentGuid, setDeploymentGuid] = useState<string>("");
  const [deploymentName, setDeploymentName] = useState<string>("");
  /**
   * The server has accepted `start_upload` for the upload in progress, so this client holds
   * the deployment's upload lock (AKL-896). Set from the manager's `onUploadStarted`, reset
   * when an upload starts or ends. The heartbeat runs only while this is true: before it
   * there is no lock to keep alive, and every beat would be a 403.
   */
  const [uploadLockHeld, setUploadLockHeld] = useState(false);
  /** The screen wake lock for this provider's uploads (AKL-889, AKL-1088). */
  const [screenWakeLock] = useState(createScreenWakeLock);
  /** The running heartbeat schedule, for the keep-alive effect's visibility handler. */
  const heartbeatRef = useRef<UploadHeartbeat | null>(null);
  const inProgress =
    !!uploadManager &&
    uploadedFileCount + skippedFileCount < selectedFiles.length;

  /**
   * A callback function to handle a file upload.
   *
   * @param hash {string} the file has
   * @param progress {number} the progress of the upload
   */
  const handleFile = (hash: string, progress: number, skipped: boolean) => {
    setUploadProgress(progress);
    if (skipped) {
      incrementSkippedFileCount();
    } else {
      incrementUploadedFileCount();
    }
  };

  /**
   * The manager gave a file up for this pass (AKL-889): it is neither uploaded nor
   * skipped, so only the progress and the not-uploaded count move.
   *
   * @param hash the file's hash
   * @param progress the progress of the upload
   * @param notUploadedCount files given up so far in this pass
   */
  const handleFileNotUploaded = (
    hash: string,
    progress: number,
    notUploadedCount: number,
  ) => {
    setUploadProgress(progress);
    setFailedFileCount(notUploadedCount);
  };

  /**
   * The manager ended its pass without completing (AKL-889). The panel stays — the
   * manager and its file list are intact — and shows "Upload stopped" with Resume and
   * Cancel instead of tearing the upload down.
   *
   * @param notUploadedCount files not uploaded in the pass that just ended
   */
  const handleUploadStopped = (notUploadedCount: number) => {
    setUploadIsPaused(false);
    setUploadIsStopped(true);
    setFailedFileCount(notUploadedCount);
    setRetryStatus(noRetry);
  };

  /**
   * The manager paused itself (AKL-891) — a freshly fetched SAS was refused, or another
   * user holds the deployment's lock. Mirrored into `uploadIsPaused` exactly as the user's
   * own `pauseUpload` below sets it, so the box reads "Upload paused" with a Resume button;
   * before this the context was never told and the box kept a Pause button over a manager
   * that was not running.
   */
  const handleUploadPaused = () => {
    setUploadIsPaused(true);
    setRetryStatus(noRetry);
  };

  /**
   * The share of the selection that is settled — uploaded or skipped — as a percentage,
   * held at 99 while anything is still owed. Mirrors the manager's own calculation minus
   * the files it has given up, which a resumed pass will try again.
   */
  const settledProgress = () => {
    if (selectedFiles.length === 0) {
      return 0;
    }
    const settled = uploadedFileCount + skippedFileCount;
    const progress = Math.min(
      Math.round((settled / selectedFiles.length) * 100),
      100,
    );
    return progress === 100 && settled < selectedFiles.length ? 99 : progress;
  };

  const incrementUploadedFileCount = () => {
    setUploadedFileCount((prev) => prev + 1);
  };

  const incrementSkippedFileCount = () => {
    setSkippedFileCount((prev) => prev + 1);
  };

  /**
   * Starts the upload process. The manager's `startUpload` never rejects by contract;
   * the handler is belt and braces so no rejection is ever left unhandled (AKL-889).
   *
   * `onUploadStarted` belongs to the component (it refreshes the History when the server
   * opens the row); the provider wraps it rather than replacing it, to learn the moment
   * this client holds the deployment's upload lock and start the heartbeat from there
   * (AKL-896, review cycle 1). Starting it from `inProgress` alone sent lockless beats
   * while the SAS fetch and `start_upload` were still pending — every one a 403 — and a
   * schedule stopped by three of them was never recreated when a later Resume finally
   * registered the upload, so the lock it then held had no heartbeat.
   * @param manager {UploadManager} the upload manager
   */
  const startUpload = (manager: UploadManager) => {
    setUploadIsPaused(false);
    setUploadIsStopped(false);
    setFailedFileCount(0);
    setRetryStatus(noRetry);
    setUploadLockHeld(false);
    setUploadManager(manager);
    manager.onFileHandled = handleFile;
    manager.onFileNotUploaded = handleFileNotUploaded;
    manager.onUploadStopped = handleUploadStopped;
    manager.onUploadPaused = handleUploadPaused;
    manager.onRetryStatus = setRetryStatus;
    const onUploadStarted = manager.onUploadStarted;
    manager.onUploadStarted = () => {
      setUploadLockHeld(true);
      onUploadStarted();
    };
    manager.isPaused = false;
    manager.startUpload().catch(console.error);
    setSelectedFiles(manager.selectedFiles);
    setProjectId(manager.getProjectId());
    setProjectGuid(manager.getProjectGuid());
    setDeploymentId(manager.getDeploymentId());
    setDeploymentGuid(manager.getDeploymentGuid());
    setDeploymentName(manager.getDeploymentName());
  };

  /**
   * Pauses the upload process.
   */
  const pauseUpload = () => {
    setUploadIsPaused(true);
    setRetryStatus(noRetry);
    uploadManager?.pauseUpload();
  };

  /**
   * Resumes a paused or stopped upload (AKL-889): the stopped state and its count are
   * cleared here, since the manager's next pass starts them afresh.
   *
   * The bar is put back to what is actually settled — uploaded plus skipped. A stopped
   * pass leaves it at 99 % (it is held there while anything is owed), and the files being
   * re-queued are not settled any more, so leaving it would claim a nearly finished
   * upload while a file waits to be tried again (review cycle 4). For a plain pause the
   * two are equal and nothing moves.
   *
   * Resume is a click, so the screen wake lock is asked for first, synchronously, in case
   * none is held (AKL-1088): Safari grants it only with user activation.
   */
  const resumeUpload = () => {
    screenWakeLock.requestInGesture();
    setUploadIsPaused(false);
    setUploadIsStopped(false);
    setFailedFileCount(0);
    setRetryStatus(noRetry);
    setUploadProgress(settledProgress());
    uploadManager?.resumeUpload();
  };

  /**
   * Cancels the upload process and resets the state.
   */
  const cancelUpload = async () => {
    endUpload();
    await uploadManager?.cancelUpload();
  };

  /**
   * The final give-up from the stopped state (AKL-889): resets this provider's state and
   * has the manager record FAILED with the honest uploaded count through AKL-890's
   * retrying helper. The outcome is returned for the caller to explain to the user.
   *
   * @returns the helper's outcome, or `null` when nothing was sent
   */
  const failUpload = async (): Promise<EndUploadOutcome | null> => {
    const manager = uploadManager;
    endUpload();
    return manager ? await manager.failUpload() : null;
  };

  /**
   * Resets the upload state.
   */
  const endUpload = () => {
    setUploadIsPaused(true);
    setUploadIsStopped(false);
    setFailedFileCount(0);
    setRetryStatus(noRetry);
    setUploadLockHeld(false);
    setSelectedFiles([]);
    setSkippedFileCount(0);
    setUploadedFileCount(0);
    setUploadProgress(0);
  };

  /**
   * The page is going away (tab closed, reloaded or navigated off): tell the server the
   * upload is cancelled, then end it locally as well. The local part matters when the
   * browser parks the page in its back/forward cache instead of destroying it: `pagehide`
   * fires either way, and a parked page keeps this provider's state and the manager's
   * loop intact. Without the teardown, pressing Back would restore an "Upload paused"
   * panel whose Resume continues uploading against a lock the beacon just released — and
   * that another user may since have taken — and would record COMPLETED over the server's
   * CANCELLED. So the manager is told to abandon the upload (it stops, detaches its
   * callbacks and releases a lock the server grants late — see `abandonUpload`), and this
   * provider's own state is reset: `startUpload` never clears the counters, so a new
   * upload begun from the restored page would otherwise inherit this one's progress. The
   * manager's own `cancelUpload()` is not used here because it posts a second,
   * non-keepalive `end_upload` that a leaving page cannot be relied on to send.
   *
   * No beacon goes for an upload that stopped before the server accepted it (AKL-823): the
   * SAS fetch or `start_upload` failed, so no run was opened and no request is in flight,
   * and an `end_upload` with no open run writes a made-up History row (AKL-1043). While
   * `start_upload` is still pending the beacon still goes, as before: the server may have
   * granted the lock by the time it arrives.
   */
  const handlePageHide = () => {
    if (!(uploadIsStopped && !uploadLockHeld)) {
      endDeploymentUploadBeacon(deploymentId, {
        status: DeploymentStatus.CANCELLED,
        file_count: uploadedFileCount,
      });
    }
    uploadManager?.abandonUpload();
    endUpload();
    setUploadManager(null);
  };

  /**
   * Keeps the record of the upload in progress current for the moment the Keycloak
   * session dies under it (AKL-891). When Keycloak refuses a token refresh, `keycloak-js`
   * fires `onAuthLogout` and then navigates the whole document to sign-in; the file list
   * and this provider go with it. `interruptedUpload` holds a snapshot outside React so
   * that event — which fires from an XHR callback — can write it to storage, and the
   * deployment's upload panel can tell the user after sign-in how much was sent.
   * `inProgress` covers the paused and stopped states too: their file list is lost just
   * the same. Cleared when the upload ends and when the provider unmounts.
   */
  const selectedFileCount = selectedFiles.length;
  useEffect(() => {
    setActiveUpload(
      inProgress
        ? {
            deploymentId,
            deploymentGuid,
            deploymentName,
            sentFileCount: uploadedFileCount,
            selectedFileCount,
          }
        : null,
    );
  }, [
    inProgress,
    deploymentId,
    deploymentGuid,
    deploymentName,
    uploadedFileCount,
    selectedFileCount,
  ]);
  useEffect(() => () => setActiveUpload(null), []);

  /**
   * Keeps the tab alive while an upload is in progress (AKL-889, runsheet fix 6).
   *
   * A Web Lock (`navigator.locks`) is held for the lifetime of `inProgress`: Chromium
   * treats a held lock as a reason not to freeze the tab, which would suspend the upload
   * loop. It is not a reason not to discard the tab — nothing a page does prevents that.
   * The lock is `shared`, so two tabs uploading at once both hold it and neither queues
   * behind the other — Chromium's `freezing_policy.cc` keys its `kHoldingWebLock` reason
   * off a boolean `IsHoldingWebLock()` and does not distinguish the modes (read from the
   * source, 2026-09-17), so a shared lock exempts the tab exactly as an exclusive one
   * would. Only a *held* lock counts, so a request left queued behind another tab buys
   * nothing: it carries an abort signal and is withdrawn in the cleanup, and its grant
   * callback checks `active` so a late grant holds nothing.
   *
   * A screen wake lock is held for the same span, through `screenWakeLock` (see
   * `screenWakeLock.ts`). Its first request is normally made earlier, in the click that
   * opened the folder picker (AKL-1088), and is kept for this upload; `hold` here asks only
   * when no lock is held or being asked for, which is what Chrome and Edge grant without a
   * gesture. The browser releases it on its own when the page is hidden or the display
   * changes; the controller asks again when the document is still visible, and
   * `visibilitychange` → visible calls `hold` again on return. The cleanup of a run that
   * held it releases it — and only such a run, so the idle run's cleanup, as the upload
   * starts, does not drop the lock the click asked for.
   *
   * The `visibilitychange` listener here is also the upload heartbeat's second trigger
   * (AKL-896): when the tab comes back into view it asks the running schedule, if there is
   * one, for a beat — the heartbeat effect below owns the schedule and leaves it in
   * `heartbeatRef` for this handler, so there is one listener, not two.
   */
  useEffect(() => {
    let active = inProgress;
    let releaseWebLock: (() => void) | undefined;
    const lockRequestAbort = new AbortController();

    if (inProgress) {
      screenWakeLock.hold();
      if (navigator.locks) {
        navigator.locks
          .request(
            UPLOAD_WEB_LOCK_NAME,
            { mode: "shared", signal: lockRequestAbort.signal },
            () => {
              if (!active) {
                return; // granted after the upload ended: hold nothing
              }
              return new Promise<void>((resolve) => {
                releaseWebLock = resolve;
              });
            },
          )
          .catch(() => {
            // the lock is best effort (and an aborted request rejects); an upload runs without it
          });
      }
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible" && inProgress) {
        screenWakeLock.hold();
        heartbeatRef.current?.beat();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      active = false;
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      lockRequestAbort.abort(); // a request still queued is withdrawn
      releaseWebLock?.();
      if (inProgress) {
        screenWakeLock.release();
      }
    };
  }, [inProgress, screenWakeLock]);

  // A lock a click asked for while idle, for an upload that never started, goes with the
  // provider (AKL-1088); the effect above releases only the lock of an upload in progress.
  useEffect(() => () => screenWakeLock.release(), [screenWakeLock]);

  /**
   * Keeps the deployment's upload lock alive while this client holds it (AKL-896): a
   * `POST /deployments/{id}/upload_heartbeat` every five minutes, and one more when the tab
   * comes back into view (asked for by the keep-alive effect's `visibilitychange` handler
   * through `heartbeatRef`), never two within a minute of each other — the rules live in
   * `uploadHeartbeat.ts`.
   *
   * The schedule runs from the moment the server accepts `start_upload` (`uploadLockHeld`,
   * set from the manager's `onUploadStarted`) until the upload ends (`inProgress`), and is
   * stopped in this cleanup — so completion, Cancel, the final give-up, the `pagehide`
   * teardown and an unmount all end it with no timer left. Not from `inProgress` alone
   * (review cycle 1): `inProgress` is true while the SAS fetch and `start_upload` are still
   * pending, and stays true through a stop or a self-pause before the server has accepted
   * anything ("another user is uploading"); beats sent then are lockless 403s, and once
   * three had stopped the schedule a later Resume that finally registered the upload found
   * no heartbeat guarding the lock it had just acquired. A paused or stopped upload that
   * *does* hold the lock keeps its heartbeat, as it keeps the two locks: its file list is
   * intact and Resume will finish the files, so its lock must not lapse under it.
   *
   * Every outcome of a heartbeat is swallowed by the call itself — the endpoint is not
   * deployed everywhere yet and answers 404 until it is — and the schedule stands down
   * after three refusals in a row. Nothing here reads or moves the upload's state.
   * `deploymentId` is read by the schedule and is a dependency; it only ever changes in
   * `startUpload`, together with `uploadLockHeld` being reset.
   */
  useEffect(() => {
    if (!inProgress || !uploadLockHeld) {
      return;
    }
    const heartbeat = startUploadHeartbeat(deploymentId);
    heartbeatRef.current = heartbeat;
    return () => {
      heartbeat.stop();
      if (heartbeatRef.current === heartbeat) {
        heartbeatRef.current = null;
      }
    };
  }, [inProgress, uploadLockHeld, deploymentId]);

  return (
    <UploadContext.Provider
      value={{
        projectId,
        deploymentId,
        deploymentGuid,
        projectGuid,
        deploymentName,
        uploadIsPaused,
        uploadIsStopped,
        failedFileCount,
        uploadAccepted: uploadLockHeld,
        retryStatus,
        inProgress,
        selectedFiles,
        uploadProgress,
        skippedFileCount,
        uploadedFileCount,
        startUpload,
        pauseUpload,
        resumeUpload,
        cancelUpload,
        failUpload,
        endUpload,
        requestScreenWakeLock: screenWakeLock.requestInGesture,
        releaseUnusedScreenWakeLock: screenWakeLock.forgoGestureRequest,
      }}
    >
      <ClosePageWarningModal
        warnBeforeClosing={inProgress}
        warningMessage={t(
          "Upload in progress. Please wait until it's finished or cancel upload.",
        )}
        // The beacon reads the token itself when the event fires (AKL-890): a token
        // captured here at render time is stale by the time a slept laptop's tab closes.
        onCloseDespiteWarning={handlePageHide}
      />
      {children}
    </UploadContext.Provider>
  );
};

export const useUpload = () => {
  const context = useContext(UploadContext);
  if (!context) {
    throw new Error("useUpload must be used within an UploadProvider");
  }
  return context;
};
